import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { openAuthedFile, isImage } from "@/lib/files";
import AuthedImage from "@/components/AuthedImage";
import { fuelPlans, wbEffective } from "@/lib/navcalc";
import { FuelParamsTable, FuelBilan } from "./FuelTables";
import { fmt, minToHm, FUEL_DENSITY } from "@/lib/units";
import WBDetailTable from "./WBDetailTable";
import WBChart from "./WBChart";
import { Eye, EyeOff, Navigation, Fuel, Scale, Ruler, FileText, Map, CheckCircle2, AlertTriangle, UserRound, Users, Radio, PlaneTakeoff } from "lucide-react";

// Read-only gallery of files attached to a briefing point/sub-item.
export function FilesGallery({ briefingId, pointId }) {
  const [files, setFiles] = useState([]);
  useEffect(() => {
    api.get(`/briefings/${briefingId}/files`)
      .then((r) => setFiles(r.data.filter((f) => String(f.point_id) === String(pointId))))
      .catch(() => {});
  }, [briefingId, pointId]);
  if (!files.length) return null;
  const imgs = files.filter((f) => isImage(f.content_type));
  const docs = files.filter((f) => !isImage(f.content_type));
  return (
    <div className="mt-2">
      {imgs.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {imgs.map((f) => (
            <AuthedImage key={f.id} path={`/briefings/${briefingId}/files/${f.id}/download`} alt={f.original_filename}
              onClick={() => openAuthedFile(`/briefings/${briefingId}/files/${f.id}/download`)}
              className="h-24 w-full cursor-pointer rounded-lg border border-slate-800 object-cover" />
          ))}
        </div>
      )}
      {docs.map((f) => (
        <button key={f.id} onClick={() => openAuthedFile(`/briefings/${briefingId}/files/${f.id}/download`)}
          className="mt-2 flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-xs text-slate-300 hover:text-cyan-300">
          <FileText className="h-3.5 w-3.5" /> {f.original_filename}
        </button>
      ))}
    </div>
  );
}

export function NavLogPreview({ nav, big }) {
  if (!nav.ret) return <NavLogPreviewTable nav={nav} big={big} />;
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-cyan-400">Aller</div>
      <NavLogPreviewTable nav={nav.out} big={big} />
      <div className="text-[11px] font-semibold uppercase tracking-wider text-cyan-400">Retour</div>
      <NavLogPreviewTable nav={nav.ret} big={big} />
      <div className="text-right font-mono-data text-xs text-cyan-200">Total A/R : {fmt(nav.totalDistance)} NM · {minToHm(nav.totalTime)} · {fmt(nav.totalFuel, 1)} L</div>
    </div>
  );
}

function NavLogPreviewTable({ nav, big }) {
  const pad = big ? "px-4 py-3" : "px-2 py-1.5";
  const tbl = big ? "text-sm" : "text-xs";
  const head = big ? "text-[11px]" : "text-[10px]";
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className={`w-full min-w-[520px] border-collapse text-left font-mono-data ${tbl}`}>
        <thead>
          <tr className={`border-b border-slate-700 bg-slate-950/60 ${head} uppercase tracking-wider text-slate-400`}>
            <th className={pad}>Point</th><th className={pad}>Cap mag</th>
            <th className={pad}>Dist</th><th className={pad}>Vsol</th>
            <th className={pad}>Temps</th><th className={pad}>Carbu</th>
          </tr>
        </thead>
        <tbody>
          {nav.rows.map((r, i) => (
            <Fragment key={i}>
            <tr className={i % 2 ? "bg-slate-900/40" : "bg-slate-900/70"}>
              <td className={`${pad} font-semibold text-slate-100`}>{r.name || `Pt ${i + 1}`}</td>
              <td className={`${pad} font-bold text-cyan-300`}>{fmt(r.headingMag)}°</td>
              <td className={`${pad} text-slate-300`}>{fmt(r.distance)}</td>
              <td className={`${pad} text-slate-300`}>{fmt(r.gs)} kt</td>
              <td className={`${pad} text-slate-300`}>{fmt(r.timeMin)}′</td>
              <td className={`${pad} text-slate-300`}>{fmt(r.fuel, 1)} L</td>
            </tr>
            {(r.frequencies || []).length > 0 && (
              <tr className="bg-slate-950/40">
                <td colSpan={6} className={big ? "px-4 py-2" : "px-2 py-1.5"}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-cyan-500/70"><Radio className="h-3 w-3" /> Fréq.</span>
                    {r.frequencies.map((f, fi) => (
                      <span key={fi} className={`inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-900/60 px-1.5 py-0.5 ${big ? "text-xs" : "text-[10px]"} text-slate-200`}>
                        <span className="font-semibold text-cyan-300">{f.type}</span>
                        {f.name && <span>{f.name}</span>}
                        {f.freq && <span className="text-emerald-300">{f.freq}</span>}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
          ))}
          <tr className="border-t border-cyan-500/30 bg-cyan-500/5 font-bold text-cyan-200">
            <td className={pad}>TOTAUX</td><td className={pad} />
            <td className={pad}>{fmt(nav.totalDistance)} NM</td><td className={pad} />
            <td className={pad}>{minToHm(nav.totalTime)}</td>
            <td className={pad}>{fmt(nav.totalFuel, 1)} L</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function FuelPreview({ briefing, nav, aircraft }) {
  const burn = Number(aircraft?.fuelBurn) || 0;
  const plans = fuelPlans(briefing, nav, aircraft);
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400"><Fuel className="h-4 w-4" /> Paramètres carburant</div>
        <FuelParamsTable plan={plans[0].plan} />
        <p className="mt-4 text-xs text-slate-500">Capacité réservoirs : {aircraft?.fuelCapacity || 0} L · Conso {burn} L/h</p>
      </div>
      <div className="space-y-4">
        {plans.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400"><Fuel className="h-4 w-4" /> Bilan carburant{p.title ? ` — ${p.title}` : ""}</div>
            <FuelBilan plan={p.plan} burn={burn} p={`pv${i}-`} />
          </div>
        ))}
      </div>
    </div>
  );
}


const StatePill = ({ label, s, mtow }) => (
  <div className={`rounded-xl border p-3 ${s.ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
      {s.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
    </div>
    <div className="mt-1 font-mono-data text-lg font-bold text-slate-100">{fmt(s.weight)} kg</div>
    <div className="font-mono-data text-[11px] text-slate-400">CG {fmt(s.cg, 3)} m{mtow && s.weight > mtow ? " · > MTOW" : ""}</div>
  </div>
);

const MarginPill = ({ label, mtow, weight }) => {
  const has = Number(mtow) > 0;
  const margin = has ? mtow - weight : 0;
  const ok = margin >= 0;
  return (
    <div className={`rounded-xl border p-3 ${ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
      </div>
      <div className={`mt-1 font-mono-data text-lg font-bold ${ok ? "text-emerald-300" : "text-rose-300"}`}>{has ? `${margin >= 0 ? "+" : ""}${fmt(margin)} kg` : "—"}</div>
      <div className="font-mono-data text-[11px] text-slate-400">{has ? (ok ? "sous MTOW" : "MTOW dépassée") : `MTOW ${fmt(mtow)} kg`}</div>
    </div>
  );
};

export function WBPreview({ briefing, aircraft, nav }) {
  const wb = wbEffective(briefing, aircraft, nav);
  const stations = aircraft?.stations || [];
  const firstStation = stations[0]?.name;
  const front = briefing.frontSeat || {};
  const pilotW = Number(front.pilot ?? briefing.pilotWeight ?? 0) || 0;
  const paxW = Number(front.pax ?? 0) || 0;
  const frontTotal = pilotW + paxW;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400"><Scale className="h-4 w-4" /> Chargement</div>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between rounded-lg bg-slate-900/40 px-3 py-2 text-sm">
              <span className="text-slate-400">Masse à vide</span>
              <span className="font-mono-data text-slate-200">{aircraft?.emptyWeight || 0} kg @ {aircraft?.emptyArm || 0} m</span>
            </div>
            {firstStation && (
              <div className="rounded-lg border border-cyan-500/20 bg-slate-900/30 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-cyan-300">{firstStation} <span className="text-slate-600">({stations[0].arm} m)</span></span>
                  <span className="font-mono-data text-sm font-semibold text-slate-100">{fmt(frontTotal)} kg</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><UserRound className="h-3 w-3" /> Pilote</div>
                    <div className="mt-1 rounded-md border border-slate-700 bg-slate-900/60 px-3 py-1.5 font-mono-data text-sm text-slate-100">{fmt(pilotW)} kg</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><Users className="h-3 w-3" /> Passager avant</div>
                    <div className="mt-1 rounded-md border border-slate-700 bg-slate-900/60 px-3 py-1.5 font-mono-data text-sm text-slate-100">{fmt(paxW)} kg</div>
                  </div>
                </div>
              </div>
            )}
            {stations.slice(1).map((s) => (
              <div key={s.name} className="flex items-center justify-between rounded-lg bg-slate-900/40 px-3 py-2 text-sm">
                <span className="text-slate-300">{s.name} <span className="text-slate-600">({s.arm} m)</span></span>
                <span className="font-mono-data text-slate-100">{fmt(Number(briefing.weights?.[s.name]) || 0)} kg</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-slate-800 px-3 pt-3 text-sm">
              <span className="text-cyan-300">Carburant départ <span className="text-slate-600">({aircraft?.fuelArm || 0} m)</span></span>
              <span className="font-mono-data text-cyan-200">{fmt(Number(briefing.fuelStartL) || 0)} L</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <MarginPill label="Marge masse déco" mtow={wb.mtow} weight={wb.takeoff.weight} />
          <StatePill label="Décollage" s={wb.takeoff} mtow={wb.mtow} />
          <StatePill label="Atterrissage" s={wb.landing} />
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400"><Scale className="h-4 w-4" /> Enveloppe de centrage</div>
        <WBChart wb={wb} aircraft={aircraft} briefing={briefing} nav={nav} height={320} />
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 xl:col-span-2">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400"><Scale className="h-4 w-4" /> Détail du calcul <span className="text-slate-500">(carburant à {FUEL_DENSITY} kg/L)</span></div>
        <WBDetailTable wb={wb} />
      </div>
    </div>
  );
}

export function LimitationsPreview({ briefing }) {
  const items = briefing.limitations || [];
  if (!items.length) return <p className="text-sm text-slate-500">Aucun terrain renseigné.</p>;
  return (
    <div className="space-y-2">
      {items.map((it, i) => {
        const toOk = Number(it.tora) >= Number(it.requiredTakeoff);
        const ldOk = Number(it.lda) >= Number(it.requiredLanding);
        return (
          <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 text-sm">
            <div className="font-mono-data font-semibold text-slate-100">{it.airport || "—"}</div>
            <div className={toOk ? "text-emerald-300" : "text-rose-300"}>Déco : {toOk ? "OK" : "INSUFFISANT"} (dispo {fmt(it.tora)} / requis {fmt(it.requiredTakeoff)} m)</div>
            <div className={ldOk ? "text-emerald-300" : "text-rose-300"}>Atterr. : {ldOk ? "OK" : "INSUFFISANT"} (dispo {fmt(it.lda)} / requis {fmt(it.requiredLanding)} m)</div>
          </div>
        );
      })}
    </div>
  );
}

const PREVIEW_META = {
  5: { icon: Navigation, label: "Aperçu Log de Nav & Carte" },
  6: { icon: Fuel, label: "Aperçu du bilan carburant" },
  7: { icon: Scale, label: "Aperçu du devis masse & centrage" },
  8: { icon: Ruler, label: "Aperçu des limitations" },
};

// Collapsible inline calculation/preview shown directly in the checklist point.
export function PointPreview({ pointId, briefing, aircraft, nav, briefingId }) {
  const [open, setOpen] = useState(true);
  const meta = PREVIEW_META[pointId];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div className="rounded-xl border border-cyan-500/25 bg-slate-900/30 p-4">
      <button onClick={() => setOpen((o) => !o)} data-testid={`preview-toggle-${pointId}`}
        className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wider text-cyan-400">
        <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {meta.label}</span>
        {open ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-3">
          {pointId === 5 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-lg border border-cyan-500/25 bg-slate-900/30 px-3 py-2" data-testid="preview-departure">
                <PlaneTakeoff className="h-4 w-4 text-cyan-400" />
                <span className="text-[11px] uppercase tracking-wider text-slate-400">Aérodrome de départ</span>
                <span className="ml-auto font-mono-data text-base font-bold text-cyan-300">{briefing.departure || "—"}</span>
              </div>
              <NavLogPreview nav={nav} />
              <div>
                <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-cyan-500/70"><Map className="h-3.5 w-3.5" /> Carte de navigation</div>
                {briefing.navMapComment && <p className="mb-2 whitespace-pre-wrap text-sm text-slate-300">{briefing.navMapComment}</p>}
                <FilesGallery briefingId={briefingId} pointId="navmap" />
              </div>
            </div>
          )}
          {pointId === 6 && <FuelPreview briefing={briefing} nav={nav} aircraft={aircraft} />}
          {pointId === 7 && <WBPreview briefing={briefing} aircraft={aircraft} nav={nav} />}
          {pointId === 8 && <LimitationsPreview briefing={briefing} />}
        </div>
      )}
    </div>
  );
}
